/**
 * Why a fragility score moved.
 *
 * The radar ranks; this says what changed. Each movement over the
 * window is split between the inputs a reading stores (blast reach, MVB
 * reach, open findings, worst severity), and whatever those do not
 * account for is named as unexplained rather than spread across them.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Bdi } from "shared/components/Bdi";
import { useFragilityChanges, type FragilityChanges } from "twin/api/fragility_hooks";

const P = "modules.twin.fragility.changes";

function isChanges(x: unknown): x is FragilityChanges {
  return !!x && Array.isArray((x as FragilityChanges).movers);
}

function value(v: number | string | null, none: string): string {
  return v === null || v === "" ? none : String(v);
}

export function FragilityChangesPanel() {
  const { t } = useTranslation();
  const [days, setDays] = useState(30);
  const q = useFragilityChanges(days);
  const data = isChanges(q.data) ? q.data : null;
  return (
    <article
      data-testid="fragility-changes"
      className="rounded-lg border border-slate-200 bg-white p-4"
    >
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-400">
          {t(`${P}.heading`)}
        </p>
        <div className="flex gap-1 text-xs">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              data-testid={`fragility-changes-days-${d}`}
              onClick={() => setDays(d)}
              className={
                d === days
                  ? "rounded bg-brand-brass/10 px-2 py-0.5 text-brand-brassDark"
                  : "rounded px-2 py-0.5 text-slate-500 hover:text-brand-brassDark"
              }
            >
              {t(`${P}.days`, { count: d })}
            </button>
          ))}
        </div>
      </div>
      <p className="mb-2 text-xs text-slate-600">{t(`${P}.why`)}</p>
      {data && data.readings < 2 && (
        <p className="text-sm text-slate-600">{t(`${P}.tooFew`)}</p>
      )}
      {data && data.readings >= 2 && data.movers.length === 0 && (
        <p data-testid="fragility-changes-none" className="text-sm text-slate-600">
          {t(`${P}.none`, { count: data.days })}
        </p>
      )}
      {data && data.movers.length > 0 && (
        <ul className="space-y-2 text-sm">
          {data.movers.map((m) => (
            <li key={`${m.entity_kind}-${m.entity_id}`} data-testid="fragility-mover">
              <p className="text-slate-900">
                <Bdi>{m.name}</Bdi>
                {": "}
                {t(m.delta > 0 ? `${P}.rose` : `${P}.fell`, {
                  from: m.from_score,
                  to: m.to_score,
                })}
              </p>
              <p className="text-xs text-slate-600">
                {[
                  ...m.parts.map((p) =>
                    t(`${P}.part`, {
                      input: t(`${P}.input.${p.input}`),
                      from: value(p.from, t(`${P}.noneValue`)),
                      to: value(p.to, t(`${P}.noneValue`)),
                      points: p.points > 0 ? `+${p.points}` : String(p.points),
                    }),
                  ),
                  ...(m.unexplained !== 0 && m.unexplained_reason
                    ? [
                        t(`${P}.unexplained.${m.unexplained_reason}`, {
                          points:
                            m.unexplained > 0
                              ? `+${m.unexplained}`
                              : String(m.unexplained),
                        }),
                      ]
                    : []),
                ].join(" · ")}
              </p>
            </li>
          ))}
        </ul>
      )}
      {data?.truncated && (
        <p className="mt-2 text-xs text-slate-500">
          {t(`${P}.truncated`, { shown: data.movers.length, total: data.moved })}
        </p>
      )}
    </article>
  );
}
